import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import TextReveal from "./animations/TextReveal";
import Magnetic from "./animations/Magnetic";

const galleryImages = [
  { id: 1,  src: "/asset/gallery/g1 (1).jpg",  title: "Nvidia Certification" },
  { id: 2,  src: "/asset/gallery/g1 (1).png",  title: "Industrial Visit to CRNS" },
  { id: 3,  src: "/asset/gallery/g1 (2).jpg",  title: "6TH Anniversary" },
  { id: 4,  src: "/asset/gallery/g1 (2).png",  title: "XTreme Session" },
  { id: 5,  src: "/asset/gallery/g1 (3).jpg",  title: "TSYP13 Awards" },
  { id: 6,  src: "/asset/gallery/g1 (4).png",  title: "StepIn IEEE Volunteering 2026" },
  { id: 7,  src: "/asset/gallery/g1 (5).png",  title: "Code of Ethics Session" },
  { id: 8,  src: "/asset/gallery/g1 (5).jpg",  title: "TSYP13" },
  { id: 9,  src: "/asset/gallery/g1 (6).jpg",  title: "TRC2.0" },
  { id: 10, src: "/asset/gallery/g1 (7).jpg",  title: "IEEE Day" },
  { id: 11, src: "/asset/gallery/g1 (8).jpg",  title: "IEEE XTreme 18.0" },
];

export default function GalleryCarousel() {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-10%" });
  const intervalRef = useRef(null);

  // Auto-advance strip
  useEffect(() => {
    if (isHovering) return;
    intervalRef.current = setInterval(() => {
      setActive((p) => (p + 1) % galleryImages.length);
    }, 3500);
    return () => clearInterval(intervalRef.current);
  }, [isHovering]);

  const prev = () => setActive((p) => (p - 1 + galleryImages.length) % galleryImages.length);
  const next = () => setActive((p) => (p + 1) % galleryImages.length);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-[#f4f7f9] py-20 md:py-28 overflow-hidden"
    >
      {/* Background texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          backgroundImage: `repeating-linear-gradient(-45deg,rgba(15,105,157,0.03) 0px,rgba(15,105,157,0.03) 1px,transparent 1px,transparent 18px),
            repeating-linear-gradient(45deg,rgba(15,105,157,0.02) 0px,rgba(15,105,157,0.02) 1px,transparent 1px,transparent 28px)`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-12">
          <TextReveal
            as="h2"
            className="text-[#006699] font-bold tracking-widest uppercase text-sm mb-2 border-l-[3px] border-[#006699] pl-3"
          >
            Our Moments
          </TextReveal>
          <TextReveal
            as="h3"
            className="text-4xl md:text-5xl lg:text-6xl font-black text-[#002233] leading-tight"
            stagger={0.06}
          >
            Through the Lens
          </TextReveal>
        </div>

        {/* Main layout: Thumbnails on the left, main image on the right */}
        <div
          className="flex flex-col md:flex-row gap-4 md:gap-6 lg:gap-8"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {/* VERTICAL thumbnail strip on the left (horizontal on mobile) */}
          <div className="flex md:flex-col gap-3 overflow-x-auto md:overflow-x-hidden md:overflow-y-auto pb-1 md:pb-0 md:pr-2 scrollbar-none flex-shrink-0 md:w-32 lg:w-40 md:max-h-[400px] lg:max-h-[500px]"
               style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {galleryImages.map((img, i) => (
              <motion.div
                key={img.id}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                onClick={() => setActive(i)}
                className="relative flex-shrink-0 w-28 md:w-full cursor-pointer group"
              >
                <div className={`relative overflow-hidden h-20 md:h-24 lg:h-28 transition-all duration-300 ${i === active ? "ring-[2px] ring-[#006699]" : "opacity-60 hover:opacity-100"}`}>
                  <img
                    src={img.src}
                    alt={img.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {i === active && (
                    <div className="absolute inset-0 bg-[#006699]/20" />
                  )}
                </div>
                <p className={`text-[10px] md:text-xs mt-1.5 truncate tracking-wide font-medium ${i === active ? "text-[#006699]" : "text-gray-400"}`}>
                  {img.title}
                </p>
              </motion.div>
            ))}
          </div>

          {/* MAIN PHOTO — Slightly reduced width and height */}
          <div className="relative w-full md:flex-1 max-w-5xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.55, ease: "easeInOut" }}
                className="relative w-full h-[280px] sm:h-[380px] md:h-[400px] lg:h-[500px] overflow-hidden cursor-pointer"
                onClick={() => setLightbox(active)}
              >
                {/* L-corners */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-[2px] border-l-[2px] border-[#006699] z-20 pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-[2px] border-r-[2px] border-[#006699] z-20 pointer-events-none" />

                <img
                  src={galleryImages[active].src}
                  alt={galleryImages[active].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#002233]/80 via-[#002233]/10 to-transparent" />

                {/* Title overlay */}
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="absolute bottom-0 left-0 right-0 p-5 md:p-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4"
                >
                  <div>
                    <p className="text-white/50 text-[10px] md:text-xs tracking-[0.25em] uppercase mb-1">
                      {String(active + 1).padStart(2, "0")} / {String(galleryImages.length).padStart(2, "0")}
                    </p>
                    <h4 className="text-white text-xl sm:text-2xl md:text-3xl font-black leading-tight">
                      {galleryImages[active].title}
                    </h4>
                    <div className="w-8 md:w-10 h-[2px] bg-[#006699] mt-2 md:mt-3" />
                  </div>

                  {/* Prev / Next inside image */}
                  <div className="flex gap-2 z-20">
                    <button
                      onClick={(e) => { e.stopPropagation(); prev(); }}
                      className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-[#006699] hover:border-[#006699] transition-all duration-300"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); next(); }}
                      className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-[#006699] hover:border-[#006699] transition-all duration-300"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6 w-full h-[2px] bg-[#006699]/10 overflow-hidden">
          <motion.div
            key={active}
            className="h-full bg-[#006699]"
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 3.5, ease: "linear" }}
          />
        </div>
      </div>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#002233]/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* L corners on lightbox */}
              <div className="absolute -top-2 -left-2 w-8 h-8 border-t-[2px] border-l-[2px] border-[#006699] z-10" />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b-[2px] border-r-[2px] border-[#006699] z-10" />

              <img
                src={galleryImages[lightbox].src}
                alt={galleryImages[lightbox].title}
                className="w-full max-h-[80vh] object-contain"
              />
              <p className="text-white text-center mt-4 font-light tracking-widest uppercase text-sm">
                {galleryImages[lightbox].title}
              </p>

              {/* Lightbox prev/next */}
              <button
                onClick={() => setLightbox((p) => (p - 1 + galleryImages.length) % galleryImages.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 border border-white/20 text-white hover:bg-[#006699] transition-all duration-300"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setLightbox((p) => (p + 1) % galleryImages.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 border border-white/20 text-white hover:bg-[#006699] transition-all duration-300"
              >
                <ChevronRight size={18} />
              </button>

              {/* Close */}
              <button
                onClick={() => setLightbox(null)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 border border-white/20 text-white hover:bg-red-500/80 transition-all duration-300 text-xl font-light"
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}